import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { supportServiceApiClient } from '@src/api/tenant/supportService/supportServiceApiClient';
import { supportServiceCategoryApiClient } from '@src/api/tenant/supportService/supportServiceCategoryApiClient';
import { supportServiceTagApiClient } from '@src/api/tenant/supportService/supportServiceTagApiClient';
import { SupportServiceForm, SupportServiceFormErrors } from '@src/api/types/supportService';
import { SupportServiceCategory } from '@src/api/types/supportServiceCategory';
import { SupportServiceTag } from '@src/api/types/supportServiceTag';
import IconImage from '@src/assets/images/common/icon/chat-action/icon-image.svg';
import { useEditorSidebar } from '@src/context/EditorSidebarContext';
import { PublicationStatus } from '@src/enum/publicationStatus';
import { useToast } from '@src/features/toast/context/toastContext';
import { useUnsavedChanges } from '@src/hooks/useUnsavedChange';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import escapeHtml from '@src/modules/utils/escapeHtml';
import { TENANT_URL } from '@src/routes/tenant';
import { TiptapEditorHandle } from '@src/ui/Editor';
import EditorHeader from '@src/ui/elements/form/EditorHeader';
import EditorSidebar from '@src/ui/templates/EditorSidebar';
import ConfirmLeaveModal from '@src/ui/templates/Shared/ConfirmLeaveModal';
import ConfirmModal from '@src/ui/templates/Shared/ConfirmModal';
import { TenantTiptapEditor } from '@src/ui/templates/Tenant/TenantTiptapEditor/TenantTiptapEditor';
import {
  convertIframeTagToSafeIframeAttr,
  convertStyleAttrToSafeStyleAttr,
  processImagesAndResize,
} from '@src/utils/editFormUtils';
import { ERROR_MESSAGES } from '@src/utils/errorMessages';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import SettingForm from './components/SettingForm';

function SupportServicesForm() {
  const [errors, setErrors] = useState<SupportServiceFormErrors>({});
  const [categoryOptions, setCategoryOptions] = useState<SupportServiceCategory[]>([]);
  const [availableTags, setAvailableTags] = useState<SupportServiceTag[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const IS_EDIT_MODE = id !== undefined;
  const oldContentRef = useRef('');

  const methods = useForm<SupportServiceForm>({
    defaultValues: {
      title: '',
      published_at: null,
      support_service_category_id: null,
      support_service_tags: [],
      description: '',
      thumbnail_url: '',
      content: '',
      is_top_display: false,
    },
  });
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const { isOpen: isOpenSideBar, setIsOpen: setIsOpenSidebar } = useEditorSidebar();

  const contentWidth = isOpenSideBar ? 'w-[calc(100%-19rem)]' : 'w-[calc(100%-68px)]';

  const openPreview = async () => {
    const { ...data } = methods.getValues();
    const stripTags = (input: string): string => {
      return input.replace(/<(?!img\b)[^>]+>/g, '');
    };
    let editorContent = await processImagesAndResize(
      oldContentRef.current,
      data.content,
      uploadFileFn,
      deleteFileFn,
    );

    editorContent = convertStyleAttrToSafeStyleAttr(editorContent);
    editorContent = convertIframeTagToSafeIframeAttr(editorContent);

    if (stripTags(editorContent).trim() === '' && !editorContent.includes('data-iframe-original')) {
      editorContent = '';
    }
    const formData = {
      ...data,
      content: escapeHtml(editorContent),
      // 登録画面の場合は強制的に下書き
      status: id ? data.status : PublicationStatus.DRAFT,
    };

    if (id) {
      const previewRes = await supportServiceApiClient.preview(+id, formData);
      handleApiResponse(
        previewRes,
        (data) => {
          const uuid = data.uuid;
          if (!uuid) {
            return;
          }
          const url = generatePath(TENANT_URL.SUPPORT_SERVICES_PREVIEW, { uuid: uuid });
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        (errors) => {
          setErrors(errors);
        },
      );
    } else {
      // 登録画面の場合、新規保存後のIDを利用してプレビューテーブルを作成して遷移
      const createResponse = await supportServiceApiClient.create(formData);
      handleApiResponse(
        createResponse,
        async (data) => {
          const previewRes = await supportServiceApiClient.preview(data.id, formData);
          handleApiResponse(
            previewRes,
            (data) => {
              const url = generatePath(TENANT_URL.SUPPORT_SERVICES_PREVIEW, {
                uuid: data.uuid,
              });
              window.open(url, '_blank', 'noopener,noreferrer');
            },
            (errors) => {
              setErrors(errors);
            },
          );
          navigate(generatePath(TENANT_URL.SUPPORT_SERVICES_EDIT, { id: data.id }));
        },
        (errors) => {
          setErrors(errors);
        },
      );
    }
  };

  const fetchDetail = async () => {
    if (!id) {
      return;
    }
    const response = await supportServiceApiClient.show(+id);
    handleApiResponse(response, (data) => {
      const supportServiceData = {
        ...data,
        support_service_category_id: data?.support_service_category?.id,
        support_service_tags: data?.support_service_tags?.map((tag) => tag?.id),
      };
      methods.reset(supportServiceData);
    });
  };

  const uploadFileFn = async (file: File) => {
    let uploadedUrl = '';
    const res = await supportServiceApiClient.uploadFile(file);
    handleApiResponse(
      res,
      (data) => {
        uploadedUrl = data.url ?? '';
      },
      (err) => {
        console.error(err);
        alert(ERROR_MESSAGES.FILE.UPLOAD_FAILED);
      },
    );
    setIsDragging(false);
    return uploadedUrl;
  };

  const deleteFileFn = async (url: string) => {
    const res = await supportServiceApiClient.deleteFile(url);
    handleApiResponse(
      res,
      () => {},
      (err) => {
        console.error('削除失敗', err);
      },
    );
  };

  const handleSave = async (status: PublicationStatus) => {
    const { ...data } = methods.getValues();
    const stripTags = (input: string): string => {
      return input.replace(/<(?!img\b)[^>]+>/g, '');
    };
    let editorContent = await processImagesAndResize(
      oldContentRef.current,
      data.content,
      uploadFileFn,
      deleteFileFn,
    );

    editorContent = convertStyleAttrToSafeStyleAttr(editorContent);
    editorContent = convertIframeTagToSafeIframeAttr(editorContent);

    if (stripTags(editorContent).trim() === '' && !editorContent.includes('data-iframe-original')) {
      editorContent = '';
    }
    const formData = {
      ...data,
      content: escapeHtml(editorContent),
      status,
    };

    setLoading(true);
    const response = IS_EDIT_MODE
      ? await supportServiceApiClient.update(+id, formData)
      : await supportServiceApiClient.create(formData);

    handleApiResponse(
      response,
      async (data) => {
        toast(
          IS_EDIT_MODE
            ? status === PublicationStatus.DRAFT
              ? TOAST_MESSAGE.UPDATE
              : TOAST_MESSAGE.REGISTER
            : status === PublicationStatus.DRAFT
              ? TOAST_MESSAGE.SAVE_DRAFT
              : TOAST_MESSAGE.REGISTER,
        );
        await supportServiceApiClient.preview(data.id, formData);
        navigate(TENANT_URL.SUPPORT_SERVICES);
        setLoading(false);
      },
      (errors) => {
        setErrors(errors);
        setIsOpenSidebar(true);
        setLoading(false);
      },
    );
    oldContentRef.current = editorContent;
  };

  const handleDelete = async () => {
    if (!id) {
      return;
    }
    setLoading(true);
    const response = await supportServiceApiClient.delete(+id);
    handleApiResponse(response, () => {
      setLoading(false);
      toast(TOAST_MESSAGE.DELETE);
      navigate(TENANT_URL.SUPPORT_SERVICES);
    });
    setLoading(false);
  };

  const loadCategories = async () => {
    const response = await supportServiceCategoryApiClient.index();
    handleApiResponse(response, (data) => {
      setCategoryOptions(data);
    });
  };

  const loadTags = async () => {
    const response = await supportServiceTagApiClient.suggest();
    handleApiResponse(response, (data) => {
      setAvailableTags(data);
    });
  };

  useEffect(() => {
    if (IS_EDIT_MODE) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IS_EDIT_MODE]);

  useEffect(() => {
    loadCategories();
    loadTags();
  }, []);

  // フォーカスをエディターに設定
  useEffect(() => {
    const timer = setTimeout(() => {
      const editor = editorRef.current?.getEditor();
      if (editor) {
        editor.commands.focus();
      }
    }, 100); // エディターの初期化を待つための短い遅延

    return () => clearTimeout(timer);
  }, []);

  const openDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    setIsDeleteModalOpen(false);
    handleDelete();
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleEditorChange = (content: string) => {
    methods.setValue('content', content);
  };

  const hasUnsavedChanges =
    !!methods.getValues('title') ||
    !!methods.getValues('content') ||
    !!methods.getValues('support_service_category_id') ||
    !!methods.getValues('support_service_tags')?.length;

  const { isModalOpen, handleReturn, confirmReturn, cancelReturn } = useUnsavedChanges({
    hasUnsavedChanges,
    onConfirm: () => {
      navigate(TENANT_URL.SUPPORT_SERVICES, { replace: true });
    },
  });

  const handleDragEnter: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
  };

  const editorRef = useRef<TiptapEditorHandle | null>(null);
  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));

      for (const file of imageFiles) {
        const uploadedUrl = await uploadFileFn(file);
        const editor = editorRef.current?.getEditor();
        if (editor) {
          const position = editor.state.selection?.$from;

          editor
            .chain()
            .setTextSelection(position?.pos ?? editor.state.doc.content.size)
            .insertContent(`<img src="${uploadedUrl}" alt="${file.name}" />`)
            .run();
        }
      }
    }
  };

  return (
    <div
      className='editor-wrapper relative flex flex-col'
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <main className='flex grow flex-col'>
        <EditorHeader
          onReturn={handleReturn}
          onPreview={openPreview}
          onSaveDraft={() => handleSave(PublicationStatus.DRAFT)}
          onPublish={() => handleSave(PublicationStatus.PUBLISHED)}
          onDelete={openDeleteModal}
          errors={errors}
          isCreateForm={!IS_EDIT_MODE}
          isDraft={methods.getValues('status') == PublicationStatus.DRAFT}
          loading={loading}
        />
        <div className='flex max-h-[calc(100vh-143px)] grow'>
          <div className='flex h-[calc(100vh-143px)] shrink-0 flex-col overflow-hidden overflow-y-auto border-r-gray-200 bg-gray-50'>
            <EditorSidebar>
              <SettingForm
                methods={methods}
                categoryOptions={categoryOptions}
                availableTags={availableTags}
                setAvailableTags={setAvailableTags}
                errors={errors}
                setErrors={setErrors}
              />
            </EditorSidebar>
          </div>
          <div className={`flex h-[calc(100vh-143px)] grow flex-col ${contentWidth}`}>
            <TenantTiptapEditor
              value={methods.getValues('content')}
              onChange={handleEditorChange}
              enabled={{
                richText: true,
                mentions: false,
                images: true,
                links: true,
                tables: true,
                iframes: true,
                textStyles: true,
                lists: true,
                codeBlocks: false,
                quotes: false,
                dividers: false,
                history: true,
                dragDrop: true,
                fileUpload: true,
                fileSave: false,
                shortcut: {
                  underline: true,
                  strike: false,
                  heading: true,
                  textAlign: true,
                  blockquote: false,
                },
              }}
              height='100%'
              width='100%'
              uploadFunction={uploadFileFn}
              ref={editorRef}
            />
          </div>
          <ConfirmLeaveModal
            isOpen={isModalOpen}
            onConfirm={confirmReturn}
            onClose={cancelReturn}
          />
          <ConfirmModal
            title='完全に削除しますか？'
            message='支援サービスが完全に削除されますがよろしいですか？'
            confirmText='完全に削除'
            cancelText='戻る'
            isOpen={isDeleteModalOpen}
            onConfirm={confirmDelete}
            onClose={cancelDelete}
            isDanger={true}
          />
        </div>
      </main>
      {isDragging && (
        <div className='pointer-events-none fixed inset-0 z-[1100] flex items-center justify-center bg-black/30'>
          <div className='flex items-center gap-2 rounded-md bg-white px-6 py-4 shadow-lg'>
            <img
              src={IconImage}
              alt='ファイルアップロード用の画像アイコン'
              className='size-5'
            />
            <p className='text-sm font-semibold'>ファイルをアップロードする</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportServicesForm;
